import { assign, sendParent, setup } from 'xstate';
import type { FilingDomainEvent } from '@/features/filing/state/events';
import type { StatementEvent } from './events';
import { handInStatementActor } from './actors';

type StatementContext = {
  input: { filingId: string; statementId: string; fileRef: string } | null;
  error: unknown;
};

type StatementEvents = StatementEvent | FilingDomainEvent;

const statementSetup = setup({
  types: {
    context: {} as StatementContext,
    events: {} as StatementEvents,
  },
  actors: { handInStatementActor },
  actions: {
    rememberInput: assign({
      input: ({ event }) =>
        event.type === 'statement.hand-in'
          ? { filingId: event.filingId, statementId: event.statementId, fileRef: event.fileRef }
          : null,
      error: () => null,
    }),
    reportError: assign({ error: ({ event }) => event }),
    emitHandedIn: sendParent(({ context }) => ({
      type: 'statement.handed-in',
      filingId: context.input?.filingId ?? '',
      statementId: context.input?.statementId ?? '',
    })),
  },
});

export const statementMachine = statementSetup.createMachine({
  id: 'statement',
  initial: 'idle',
  context: { input: null, error: null },
  on: {
    'statement.hand-in': { target: '.handingIn', actions: 'rememberInput' },
    'filing.opened': { target: '.idle' },
  },
  states: {
    idle: {},
    handingIn: {
      invoke: {
        src: 'handInStatementActor',
        input: ({ context }) => context.input ?? { filingId: '', statementId: '', fileRef: '' },
        onDone: {
          target: 'idle',
          actions: [
            'emitHandedIn',
            sendParent({ type: 'ui.toast.push', toast: { title: 'Statement handed in', tone: 'success' } }),
          ],
        },
        onError: {
          target: 'error',
          actions: [
            'reportError',
            sendParent({ type: 'ui.toast.push', toast: { title: 'Statement could not be handed in', tone: 'error' } }),
          ],
        },
      },
    },
    error: {
      on: { 'statement.hand-in': { target: 'handingIn', actions: 'rememberInput' } },
    },
  },
});
